import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { formatCurrency as formatCurrencyBase } from "../lib/calculations";

// Claim-report figures always show 2 decimals ($x.yy) per Josh's request; wrap
// the shared formatter so every currency value in this summary uses them.
const formatCurrency = (amount: number | undefined | null): string =>
  formatCurrencyBase(amount, true);

interface ProjectSummarySectionProps {
  contractValue: number;
  revisedContractValue: number;
  progressValue: number;
  totalApprovedVariations: number;
  totalWaitingVariations: number;
  discountsTotal?: number;
  retentionTotal?: number;
  retentionCeiling?: number;
  retentionMode?: string;
  retentionReceived?: number;
  projectId?: string;
  onRetentionBulkSaved?: () => void;
}

export function ProjectSummarySection({
  contractValue,
  revisedContractValue,
  progressValue,
  totalApprovedVariations,
  totalWaitingVariations,
  discountsTotal = 0,
  retentionTotal,
  retentionCeiling,
  retentionMode,
  retentionReceived = 0,
}: ProjectSummarySectionProps) {
  // Doughnut data: Progress Claims vs Remaining (no waiting)
  const doughnutData = useMemo(() => {
    const claimed = progressValue;
    // User requested "Remaining" to be solely based on Original Contract
    const remaining = Math.max(0, contractValue - claimed);

    return [
      {
        name: "Progress Claims",
        value: claimed,
        color: "url(#summaryColorProgress)",
      },
      { name: "Remaining", value: remaining, color: "#f1f5f9" },
    ];
  }, [contractValue, progressValue]);

  // Use Original Contract Value for percentage calculation as requested
  const baseValue = contractValue > 0 ? contractValue : 1;
  const claimedPercentage = (progressValue / baseValue) * 100;

  const isOver = claimedPercentage > 100;
  // Determine chart colors based on percentage:
  // - Green when ≤100% (within budget)
  // - Red when >100% (over budget)
  const chartColor = isOver ? "#dc2626" : "#16a34a"; // red or green
  const chartColorLight = isOver ? "#ef4444" : "#22c55e"; // lighter shade
  const hasApprovedVariations = totalApprovedVariations !== 0; // Can be negative (credits)
  const totalVariations = totalApprovedVariations + totalWaitingVariations;
  const hasDiscounts = discountsTotal > 0;

  return (
    <div className="project-summary-grid">
      {/* Doughnut Chart - Left Side */}
      <div
        className="card"
        style={{
          padding: "var(--spacing-lg)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h4
          style={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "var(--text-muted)",
            marginBottom: "var(--spacing-sm)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Progress Claims
        </h4>

        <div style={{ width: "100%", height: 220, position: "relative" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                <linearGradient
                  id="summaryColorProgress"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={chartColorLight} />
                  <stop offset="100%" stopColor={chartColor} />
                </linearGradient>
              </defs>
              <Pie
                data={doughnutData}
                cx="50%"
                cy="50%"
                innerRadius={68}
                outerRadius={88}
                paddingAngle={3}
                dataKey="value"
                stroke="none"
                cornerRadius={6}
              >
                {doughnutData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => formatCurrency(Number(value) || 0)}
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  padding: "8px 12px",
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Center percentage */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <span
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: chartColor,
              }}
            >
              {claimedPercentage.toFixed(2)}%
            </span>
            <span
              style={{
                fontSize: "0.7rem",
                color: "var(--text-muted)",
                fontWeight: 500,
              }}
            >
              Claimed
            </span>
          </div>
        </div>

        {/* Legend */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginTop: "var(--spacing-sm)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: chartColor,
              }}
            />
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
              Claimed
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#e2e8f0",
              }}
            />
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
              Remaining
            </span>
          </div>
        </div>
      </div>

      {/* Financial Cards - Right Side */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-md)",
        }}
      >
        {/* Contract Value Card */}
        <div
          className="card"
          style={{
            padding: "var(--spacing-md) var(--spacing-lg)",
            border: "2px solid var(--color-primary)",
            background: "rgba(255, 107, 53, 0.03)",
          }}
        >
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--color-primary)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 2,
            }}
          >
            {hasApprovedVariations
              ? "Contract Value (Revised)"
              : "Contract Value"}
          </div>
          <div data-seq-private=""
            style={{
              fontSize: "1.5rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {formatCurrency(revisedContractValue)}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              marginTop: 4,
            }}
          >
            {/* Show breakdown if discounts or variations exist */}
            {(hasDiscounts || hasApprovedVariations) && (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "var(--spacing-sm)",
                    maxWidth: "280px",
                    width: "100%",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>Original Contract Value:</span>
                  <span data-seq-private="" style={{ whiteSpace: "nowrap" }}>{formatCurrency(contractValue)}</span>
                </div>
                {hasDiscounts && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--color-warning)",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "var(--spacing-sm)",
                      maxWidth: "280px",
                      width: "100%",
                    }}
                  >
                    <span style={{ whiteSpace: "nowrap" }}>Less Discounts:</span>
                    <span data-seq-private="" style={{ whiteSpace: "nowrap" }}>-{formatCurrency(discountsTotal)}</span>
                  </div>
                )}
              </>
            )}
            {hasApprovedVariations && (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--color-success)",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "var(--spacing-sm)",
                  maxWidth: "280px",
                  width: "100%",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>Variations:</span>
                <span style={{ whiteSpace: "nowrap" }}>
                  {totalApprovedVariations >= 0 ? "+" : ""}
                  {formatCurrency(totalApprovedVariations)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Progress Claims Card */}
        <div
          className="card"
          style={{
            padding: "var(--spacing-md) var(--spacing-lg)",
            border: "1px solid rgba(59, 130, 246, 0.35)",
          }}
        >
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 2,
            }}
          >
            Progress Claims
          </div>
          <div data-seq-private=""
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {formatCurrency(progressValue)}
          </div>
        </div>

        {/* Retention Held Card */}
        {retentionTotal !== undefined && retentionCeiling !== undefined && (() => {
          const outstanding = Math.max(0, retentionTotal - retentionReceived);
          const borderColor = outstanding === 0 ? "#22c55e" : retentionTotal >= retentionCeiling ? "#dc2626" : "#22c55e";
          return (
          <div
            className="card"
            style={{
              padding: "var(--spacing-md) var(--spacing-lg)",
              border: `1px solid ${borderColor}`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 2,
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <span>Retention Held</span>
              {outstanding === 0 ? (
                <span style={{ color: "#22c55e", fontWeight: 700 }}>Fully Released</span>
              ) : retentionTotal >= retentionCeiling ? (
                <span style={{ color: "#dc2626" }}>Ceiling Reached</span>
              ) : null}
            </div>
            <div data-seq-private=""
              style={{
                fontSize: "1.25rem",
                fontWeight: 700,
                color: outstanding === 0 ? "#22c55e" : "var(--text-primary)",
              }}
            >
              {formatCurrency(outstanding)}
            </div>
            <div
              style={{
                display: "flex",
                gap: "var(--spacing-sm)",
                marginTop: 6,
                fontSize: "0.72rem",
                color: "var(--text-muted)",
                flexWrap: "wrap",
              }}
            >
              <span data-seq-private="" style={{ whiteSpace: "nowrap" }}>Total: {formatCurrency(retentionTotal)}</span>
              <span style={{ whiteSpace: "nowrap", opacity: 0.6 }}>|</span>
              <span data-seq-private="" style={{ whiteSpace: "nowrap" }}>Ceiling: {formatCurrency(retentionCeiling)}</span>
              <span style={{ whiteSpace: "nowrap", opacity: 0.6 }}>|</span>
              <span style={{ whiteSpace: "nowrap" }}>Based on: {retentionMode === 'revised' ? 'Revised Contract' : retentionMode === 'custom' ? 'Custom Value' : 'Original Contract'}</span>
            </div>
            {retentionTotal! > 0 && (
              <>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span data-seq-private=""
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: "4px",
                      background: retentionReceived > 0 ? "rgba(34,197,94,0.1)" : "rgba(100,116,139,0.08)",
                      color: retentionReceived > 0 ? "#22c55e" : "var(--text-muted)",
                      border: `1px solid ${retentionReceived > 0 ? "rgba(34,197,94,0.2)" : "transparent"}`,
                    }}
                  >
                    Received: {formatCurrency(retentionReceived)}
                  </span>
                  {retentionReceived >= retentionTotal! && (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: "4px",
                        background: "rgba(34,197,94,0.12)",
                        color: "#22c55e",
                        border: "1px solid rgba(34,197,94,0.25)",
                      }}
                    >
                      Fully Released
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          );
        })()}

        {/* Variations Card */}
        <div
          className="card"
          style={{
            padding: "var(--spacing-md) var(--spacing-lg)",
            border: "1px solid rgba(34, 197, 94, 0.35)",
          }}
        >
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 2,
            }}
          >
            Variations
          </div>
          <div data-seq-private=""
            style={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "var(--text-primary)",
            }}
          >
            {formatCurrency(totalVariations)}
          </div>
          <div
            style={{
              display: "flex",
              gap: "var(--spacing-md)",
              marginTop: 4,
              fontSize: "0.75rem",
              flexWrap: "wrap",
            }}
          >
            <span data-seq-private="" style={{ color: "var(--color-success)", whiteSpace: "nowrap" }}>
              ✓ Approved: {formatCurrency(totalApprovedVariations)}
            </span>
            <span data-seq-private="" style={{ color: "var(--color-warning)", whiteSpace: "nowrap" }}>
              ◷ Waiting: {formatCurrency(totalWaitingVariations)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
